import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
} from "react"
import type {
  FC,
  ReactNode,
} from "react"
import { useRefValue } from "src/hooks/utils"

export type ReloadState = {
  requestReload: (id: Follow["id"]) => void;
  reloadRequests: Record<Follow["id"], string>; // string is a date string
}

interface Props {
  children?: ReactNode;
}

export type UseAggregateReloadingContext = () => ReloadState

/*
 * this creates a context that is consumed by paginated loaders
 *
 * when the `requestReload` function is called, the timestamp for a specfic
 * follow ID is updated. the paginated loader responds to this change by
 * re-executing the loader.
 */
export function createAggregateReloadingContextProvider(): [
  FC<Props>,
  UseAggregateReloadingContext,
] {
  const AggregateReloadingContext = createContext<ReloadState>({
    reloadRequests: {},

    requestReload: () => {},
  })

  const useAggregateReloadingContext = () => useContext(AggregateReloadingContext)

  const AggregateReloadingContextProvider = (props: Props) => {
    const [
      reloadRequests,
      setReloadRequests,
    ] = useState<ReloadState["reloadRequests"]>({})

    const reloadRequestsRef = useRefValue(reloadRequests)

    // the requestReload value should never change because its only
    // dependencies are setState and a ref
    const requestReload = useCallback(
      (id: Follow["id"]) => {
        setReloadRequests({
          ...reloadRequestsRef.current,
          [id]: dayjs().toISOString(),
        })
      },
      [
        reloadRequestsRef,
        setReloadRequests,
      ],
    )

    const reloadingState = useMemo(
      () => ({
        reloadRequests,
        requestReload,
      }),
      [
        reloadRequests,
        requestReload,
      ],
    )

    return (
      <AggregateReloadingContext.Provider value={reloadingState}>
        {props.children}
      </AggregateReloadingContext.Provider>
    )
  }

  return [
    AggregateReloadingContextProvider,
    useAggregateReloadingContext,
  ]
}
